import {
  RouteComponentProps,
  useParams,
  useLocation,
  useHistory,
} from 'react-router-dom';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import { get, debounce } from 'lodash';
import {
  Icon,
  Button,
  HideText,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { toast } from 'react-toastify';
import ReactVirtualizedTable from '../ReusableComponents/Table/TableFull/VirtualizedTable';
import {
  useSubmitBulkItemLeadTimes,
  useGetTodoUserItemLeadTimes,
  useGetCompletedUserItemLeadTimes,
  useGetOpenChatItemLeadTimes,
  useSaveItemLeadTimes,
} from '../../Lib/Hooks/item-lead-time.hooks';
import {
  ItemLeadTime,
  ItemLeadTimeErrors,
  EditedItemLeadTime,
  CompletedItemLeadTime,
} from './types';
import {
  FieldsValidationSchema,
  UpdateOneSchema,
  UpdateBulkSchema,
} from './validation';
import { ReactComponent as FileCheckSVG } from '../../Assets/icons/file-check.svg';
import {
  RequestedItemTableRowEditable,
  CompletedItemTableRow,
  FormCoo,
  FormExclusivity,
  FormShipmentMode,
} from './legacy';
import {
  FormComments,
  FormCustomMOQJr,
  FormCustomMOQPlus,
  FormEarliestCustomCut,
  FormEarliestIHD,
  FormIncomingStockDate,
  FormIncomingStockQuantity,
  FormIncomingStockRatio,
  FormStockQuantity,
  FormStockRatio,
  FormCustomCutLeadTime,
} from './legacy/ColumnForm';
import styles from './legacy/RequestedItems.module.css';
import {
  SubmitBulkItemLeadTimeData,
  SortDirection,
} from '../../Lib/Api/item-lead-time';
import ItemRequestDataGrid from './ItemRequestDataGrid';
import { useRowSelection } from './ItemRequestDataGrid/hooks';
import { itemLeadTimesExport } from '../../Lib/Utils/Table/table-export-v2';
import Page from '../Page';
import Pagination from '../ReusableComponents/PagePagination';
import Header from '../Header';
import { User } from '../../Lib/Api/vendor';
import { Actions, hasPermissionTo, RolesType } from '../../Lib/Constants/roles';
import { BASELINE_COST_TOOLTIP } from '../../Lib/Constants/ui-text';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import CountryList from '../../Lib/Constants/countries';
import { RequestedItemsProvider } from './context/RequestedItemsContext';
import { useRequestedItemsContext } from './hooks/useRequestedItems';
import {
  useRequestedItemsUrlParams,
  type RequestedItemsTab,
} from './hooks/useRequestedItemsUrlParams';
import { RequestedItemsDrawer } from './RequestedItemsDrawer';
import { normalizeSubmissionData } from './utils/normalizeSubmissionData';
import { BulkActionToolbar } from './BulkActionToolbar';
import { BULK_FIELD_OPTIONS } from './bulk-field-config';
// Stable no-op functions to prevent unnecessary re-renders in CompletedItems DataGrid
const NOOP_UPDATE_ITEM = () => {};
const NOOP_ERROR = () => {};
const EMPTY_EDITED_ITEMS: EditedItemLeadTime[] = [];
const EMPTY_ERRORS: Record<string, ItemLeadTimeErrors> = {};

type RequestedItemsEmptyStateVariant = 'allCompleted' | 'noSearchResults';

function RequestedItemsEmptyState({
  variant,
}: {
  variant: RequestedItemsEmptyStateVariant;
}) {
  if (variant === 'allCompleted') {
    const completedText = "You've completed all requested items.";
    const emailText = "We'll email you as soon as we need more information";
    return (
      <Page.Content>
        <Stack direction="col" align="center">
          <FileCheckSVG />
          <div className={styles.allItemsCompletedDiv}>
            <h3>{completedText}</h3>
            <p>{emailText}</p>
            <p>Thank you.</p>
          </div>
        </Stack>
      </Page.Content>
    );
  }
  return (
    <Page.Content>
      <Stack direction="col" align="center">
        <Icon
          name="inbox/inbox-default"
          variant="solid"
          size="32px"
          className="text-neutral-400"
        />
        <div className={styles.allItemsCompletedDiv}>
          <h3>No items found</h3>
          <p>
            Try adjusting the style or display name, or clear your search to see
            all items.
          </p>
        </div>
      </Stack>
    </Page.Content>
  );
}

type PageHeadersProps = {
  currentUser: User;
  userId: string;
  requestedCount: number;
  completedCount: number;
  openChatCount?: number;
  setTab: (tab: string) => void;
  tabValue: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  submitAllButton?: React.ReactNode;
  showReorderChat?: boolean;
  bulkActionToolbar?: React.ReactNode;
};

function PageHeaders({
  currentUser,
  userId,
  requestedCount,
  completedCount,
  openChatCount,
  setTab,
  tabValue,
  searchValue = '',
  onSearchChange,
  submitAllButton,
  showReorderChat = false,
  bulkActionToolbar,
}: PageHeadersProps) {
  const perms = useMemo(() => {
    const canExport = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.READ,
      'EXPORT',
    );
    const canSubmit = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.CREATE,
      'SUBMIT_ALL',
    );
    return { canExport, canSubmit };
  }, [currentUser]);

  const filterItems = useMemo(
    () => [
      {
        icon: 'three-dots-menu/three-dots-menu-horizontal-circle' as const,
        value: 'needs_details',
        label: 'Needs Details',
        count: requestedCount ?? undefined,
      },
      ...(showReorderChat
        ? [
            {
              icon: 'chat/chat-default' as const,
              value: 'open_chats',
              label: 'Open Chats',
              count: openChatCount ?? undefined,
            },
          ]
        : []),
      {
        icon: 'check/check-tick-circle' as const,
        value: 'completed',
        label: 'Completed',
        count: completedCount ?? undefined,
      },
    ],
    [requestedCount, completedCount, openChatCount, showReorderChat],
  );

  return (
    <>
      <Header.Major icon="inbox/inbox-default" title="Item Request Inbox">
        {perms.canSubmit ? submitAllButton : null}
      </Header.Major>
      <Header.Minor
        search={
          onSearchChange
            ? {
                value: searchValue,
                onChange: onSearchChange,
                placeholder: 'Search by style or display name..',
                position: 'left',
                className: 'min-w-72',
              }
            : undefined
        }
        filter={{
          value: tabValue,
          onChange: (tab) => setTab(tab),
          items: filterItems,
        }}
      >
        {bulkActionToolbar}
        {perms.canExport && (
          <Button
            onClick={() => itemLeadTimesExport(userId)}
            size="sm"
            color="secondary"
            disabled={completedCount + requestedCount === 0}
          >
            <Icon name="download/download-down" />
            <HideText notAbove="lg">Export</HideText>
          </Button>
        )}
      </Header.Minor>
    </>
  );
}

const checkReady = (item: EditedItemLeadTime) => {
  const isAllRequiredPropsFilled = UpdateOneSchema.validate(item, {
    allowUnknown: true,
  });

  if (isAllRequiredPropsFilled.error) {
    return false;
  }
  return true;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
interface _ItemLeadTimesProps {
  placeholder?: string;
  user: User | Record<string, unknown>;
  currentUser: User;
}

type ItemLeadTimesProps = _ItemLeadTimesProps & RouteComponentProps;

type RequestedItemsProps = {
  currentUser: User;
  userId: string;
  vendorId: string;
  domesticEligible?: boolean;
  /** Controls data source and empty-state UI. Defaults to 'todo'. */
  variant?: 'todo' | 'open_chats';
  pageHeaderInfo: {
    requestedCount: number;
    completedCount: number;
    openChatCount?: number;
    setTab: (tab: string) => void;
    tabValue: string;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
  };
  /** Optional controlled page (e.g. from URL). When set, displayed page uses this value; pass onChangePage so pagination updates are applied. */
  page?: number;
  onChangePage?: (page: number) => void;
  /** Search term for API filter (style/display name). */
  search?: string;
  onRowClick?: (worksheetId: string) => void;
  onMessageClick?: (worksheetId: string) => void;
  isDrawerOpen?: boolean;
  showReorderChat?: boolean;
  sort: SortDirection;
  onSortChange: (sort: SortDirection) => void;
};

function useSaveItemDebounce(val: EditedItemLeadTime[], delay: number) {
  const [debounceValue, setDebounceValue] = useState(val);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceValue(val);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [val, delay]);
  return debounceValue;
}

const DEFAULT_PAGE_SIZE = 50;

function RequestedItems(props: RequestedItemsProps) {
  const {
    userId,
    vendorId,
    pageHeaderInfo,
    currentUser,
    domesticEligible,
    variant = 'todo',
    page: controlledPage,
    onChangePage: controlledOnChangePage,
    search: searchParam,
    onRowClick,
    onMessageClick,
    isDrawerOpen,
    showReorderChat = false,
    sort,
    onSortChange,
  } = props;
  if (!userId) throw new Error('Missing id path parameter');
  const [errors, setErrors] = useState<Record<string, ItemLeadTimeErrors>>({});
  const { flags } = useContext(FeatureFlagsContext);
  const {
    setAllItems,
    setSelectedItem,
    selectedItem,
    setPerms,
    closeDrawer,
    setDomesticEligible,
  } = useRequestedItemsContext();

  const [internalPage, setInternalPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [editedItems, setEditedItems] = useState<EditedItemLeadTime[]>([]);
  const [readyArray, setReadyArray] = useState<Record<string, boolean>>({});

  const hasControlledPage = controlledPage !== undefined;
  const page = hasControlledPage ? (controlledPage ?? 1) : internalPage;
  const setPage =
    controlledPage === undefined
      ? setInternalPage
      : (controlledOnChangePage ?? (() => {}));

  const hasWarnedNoOnChangePage = React.useRef(false);
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      controlledPage !== undefined &&
      controlledOnChangePage == null &&
      !hasWarnedNoOnChangePage.current
    ) {
      hasWarnedNoOnChangePage.current = true;
      // eslint-disable-next-line no-console
      console.warn(
        'RequestedItems: page is provided without onChangePage; pagination changes will have no effect.',
      );
    }
  }, [controlledPage, controlledOnChangePage]);

  // Reset page and clear edited items when userId, variant, or showReorderChat changes (only for uncontrolled)
  useEffect(() => {
    if (controlledPage === undefined) {
      setInternalPage(1);
    }
    setEditedItems([]);
    setReadyArray({});
  }, [userId, variant, showReorderChat, controlledPage]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPage],
  );

  // Both hooks are always called (rules of hooks) but only the active variant fires
  const todoQuery = useGetTodoUserItemLeadTimes({
    userId,
    page,
    limit: pageSize,
    sort,
    search: searchParam,
    enabled: variant === 'todo',
  });
  const openChatsQuery = useGetOpenChatItemLeadTimes({
    userId,
    page,
    limit: pageSize,
    sort,
    search: searchParam,
    enabled: variant === 'open_chats',
  });
  const { isLoading, data: response } =
    variant === 'todo' ? todoQuery : openChatsQuery;
  const items = response?.items;
  const totalCount = response?.count ?? 0;

  const { isLoading: isSubmitting, mutate } = useSubmitBulkItemLeadTimes();
  const { mutate: saveItem } = useSaveItemLeadTimes();

  const pageItemIds = useMemo(
    () => (items ?? []).map((item: ItemLeadTime) => item.worksheet_id),
    [items],
  );

  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clearSelection,
    isAllSelected,
    isPartiallySelected,
    selectedCount,
  } = useRowSelection({ pageItemIds, page, pageSize });

  const countrySelectItems = useMemo(
    () => [
      { value: '', label: 'Select..' },
      ...CountryList.map((country) => ({
        value: country.text,
        label: country.text,
        disabled: !domesticEligible && country.value === 'US',
      })),
    ],
    [domesticEligible],
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  useEffect(() => {
    setDomesticEligible(!!domesticEligible);
  }, [domesticEligible, setDomesticEligible]);

  const selectedItemId = selectedItem?.worksheet_id;
  useEffect(() => {
    if (items && items.length === 0) {
      setAllItems([]);
      closeDrawer();
      setSelectedItem(null);
    }
  }, [items, setAllItems, setSelectedItem, closeDrawer]);

  const itemsToSave = useSaveItemDebounce(editedItems, 2000);
  useEffect(() => {
    if (itemsToSave.length === 0) return;
    saveItem({
      userId,
      items: itemsToSave,
    });
  }, [itemsToSave, saveItem, userId]);

  useEffect(() => {
    if (items) {
      setErrors((oldErrors) =>
        items.reduce<Record<string, ItemLeadTimeErrors>>(
          (acc, item: ItemLeadTime) => {
            const err = Object.keys(oldErrors).find(
              (key) => key === item.worksheet_id,
            );
            if (err) {
              return {
                ...acc,
                [item.worksheet_id]: oldErrors[err],
              };
            }
            return {
              ...acc,
              [item.worksheet_id]: {},
            };
          },
          {},
        ),
      );
      setReadyArray((oldArray) =>
        items.reduce<Record<string, boolean>>((acc, item: ItemLeadTime) => {
          const current = Object.keys(oldArray).find(
            (key) => key === item.worksheet_id,
          );
          if (current !== undefined) {
            return {
              ...acc,
              [item.worksheet_id]: oldArray[current],
            };
          }
          return {
            ...acc,
            [item.worksheet_id]: checkReady(item),
          };
        }, {}),
      );
    }
  }, [items]);

  const isSuccessPage = useMemo(
    () => !isLoading && (!items || items.length <= 0) && totalCount === 0,
    [items, totalCount, isLoading],
  );

  const isFullReady = useMemo(
    () => Object.values(readyArray).every((value) => value),
    [readyArray],
  );

  const handleError = useCallback(
    (worksheetId: string, itemErrors: ItemLeadTimeErrors) => {
      setErrors((oldErrors) => ({
        ...(oldErrors || {}),
        [worksheetId]: {
          ...itemErrors,
        },
      }));
    },
    [],
  );

  const handleSubmitAllItems = useCallback(() => {
    if (editedItems.length === 0) return;

    let oneHasError = false;
    const values = editedItems
      .map((item) => {
        const result = UpdateBulkSchema.validate(item, {
          abortEarly: false,
          stripUnknown: true,
        });
        if (result.error) {
          handleError(
            item.worksheet_id,
            result.error.details.reduce((acc, detail) => {
              if (detail.context?.key) {
                return {
                  ...acc,
                  [detail.context.key]: {
                    isInvalid: true,
                    message: detail.message,
                  },
                };
              }
              return acc;
            }, {}),
          );
          oneHasError = true;
          return undefined;
        }
        handleError(item.worksheet_id, {});

        return normalizeSubmissionData(result.value);
      })
      .filter((item) => item !== undefined);

    if (!oneHasError) {
      mutate({
        userId,
        items: values,
      });
    }
  }, [mutate, editedItems, handleError, userId]);

  const handleItemValidation = useCallback(
    (updatedItem: ItemLeadTime) => {
      setReadyArray((oldArray) =>
        Object.keys(oldArray).reduce((acc, worksheet_id) => {
          if (updatedItem.worksheet_id === worksheet_id) {
            const existingEdits = editedItems.find(
              (item) => item.worksheet_id === worksheet_id,
            );
            const mergedItem = {
              ...updatedItem,
              ...existingEdits,
            };
            return {
              ...acc,
              [worksheet_id]: checkReady(mergedItem),
            };
          }
          return {
            ...acc,
            [worksheet_id]: oldArray[worksheet_id],
          };
        }, {}),
      );
    },
    [editedItems],
  );

  const handleUpdateItem = useCallback(
    (updatedItem: Partial<ItemLeadTime> & { worksheet_id: string }) => {
      setEditedItems((oldEditedItems) => {
        const existingItemIndex = oldEditedItems.findIndex(
          (item) => item.worksheet_id === updatedItem.worksheet_id,
        );

        let newEditedItems: EditedItemLeadTime[];
        if (existingItemIndex !== -1) {
          newEditedItems = oldEditedItems.map((item) => {
            if (item.worksheet_id === updatedItem.worksheet_id) {
              return {
                ...item,
                ...updatedItem,
              };
            }
            return item;
          });
        } else {
          newEditedItems = [...oldEditedItems, updatedItem];
        }

        const mergedEditedItem = newEditedItems.find(
          (item) => item.worksheet_id === updatedItem.worksheet_id,
        );
        const originalItem = items?.find(
          (item: ItemLeadTime) =>
            item.worksheet_id === updatedItem.worksheet_id,
        );
        if (originalItem && mergedEditedItem) {
          const fullMergedItem = {
            ...originalItem,
            ...mergedEditedItem,
          };
          setReadyArray((oldArray) => ({
            ...oldArray,
            [updatedItem.worksheet_id]: checkReady(fullMergedItem),
          }));
        }

        return newEditedItems;
      });
    },
    [items],
  );

  const handleUpdateAllItems = useCallback(
    <C extends keyof ItemLeadTime>(column: C, value: ItemLeadTime[C]) => {
      const result = FieldsValidationSchema[column].validate(value, {
        abortEarly: false,
      });
      setErrors((oldErrors: Record<string, ItemLeadTimeErrors>) =>
        Object.keys(oldErrors).reduce(
          (acc, key) => ({
            ...acc,
            [key]: {
              ...oldErrors[key],
              [column]: result.error
                ? {
                    message: result.error.details[0].message,
                    isInvalid: true,
                  }
                : undefined,
            },
          }),
          {},
        ),
      );

      setEditedItems((oldEditedItems) => {
        const currentPageIds = new Set(
          (items ?? []).map((item: ItemLeadTime) => item.worksheet_id),
        );

        const otherPageEdits = oldEditedItems.filter(
          (edit) => !currentPageIds.has(edit.worksheet_id),
        );

        const currentPageEdits: EditedItemLeadTime[] = (items ?? []).map(
          (item: ItemLeadTime) => {
            const editedItemFound = oldEditedItems.find(
              (editedItem) => editedItem.worksheet_id === item.worksheet_id,
            );
            if (editedItemFound) {
              return {
                ...editedItemFound,
                [column]: value === '' ? null : value,
              };
            }
            return {
              worksheet_id: item.worksheet_id,
              [column]: value === '' ? null : value,
            };
          },
        );

        const newEditedItems = [...otherPageEdits, ...currentPageEdits];

        setReadyArray((oldReadyArray) => {
          const newReadyArray = { ...oldReadyArray };
          currentPageEdits.forEach((editedItem) => {
            const originalItem = (items ?? []).find(
              (item: ItemLeadTime) =>
                item.worksheet_id === editedItem.worksheet_id,
            );
            if (originalItem) {
              const fullMergedItem = {
                ...originalItem,
                ...editedItem,
              };
              newReadyArray[editedItem.worksheet_id] =
                checkReady(fullMergedItem);
            }
          });
          return newReadyArray;
        });

        return newEditedItems;
      });
    },
    [items],
  );

  const handleUpdateSelectedItems = useCallback(
    <C extends keyof ItemLeadTime>(column: C, value: ItemLeadTime[C]) => {
      const result = FieldsValidationSchema[column].validate(value, {
        abortEarly: false,
      });

      const selectedItemsList = (items ?? []).filter((item: ItemLeadTime) =>
        selectedIds.has(item.worksheet_id),
      );

      if (selectedItemsList.length === 0) {
        toast('Selected items are no longer available', { type: 'warning' });
        clearSelection();
        return;
      }

      setErrors((oldErrors: Record<string, ItemLeadTimeErrors>) => {
        const newErrors = { ...oldErrors };
        selectedIds.forEach((id) => {
          if (result.error) {
            newErrors[id] = {
              ...(newErrors[id] ?? {}),
              [column]: {
                message: result.error.details[0].message,
                isInvalid: true,
              },
            };
          } else if (newErrors[id]) {
            const updatedErrors = { ...newErrors[id] };
            delete updatedErrors[column];
            newErrors[id] = updatedErrors;
          }
        });
        return newErrors;
      });

      setEditedItems((oldEditedItems) => {
        const updatedEdits = selectedItemsList.map((item: ItemLeadTime) => {
          const existing = oldEditedItems.find(
            (e) => e.worksheet_id === item.worksheet_id,
          );
          if (existing) {
            return { ...existing, [column]: value === '' ? null : value };
          }
          return {
            worksheet_id: item.worksheet_id,
            [column]: value === '' ? null : value,
          };
        });

        const updatedIds = new Set(updatedEdits.map((e) => e.worksheet_id));
        const unchanged = oldEditedItems.filter(
          (e) => !updatedIds.has(e.worksheet_id),
        );
        const newEditedItems = [...unchanged, ...updatedEdits];

        setReadyArray((oldReadyArray) => {
          const newReadyArray = { ...oldReadyArray };
          updatedEdits.forEach((editedItem) => {
            const originalItem = (items ?? []).find(
              (item: ItemLeadTime) =>
                item.worksheet_id === editedItem.worksheet_id,
            );
            if (originalItem) {
              newReadyArray[editedItem.worksheet_id] = checkReady({
                ...originalItem,
                ...editedItem,
              });
            }
          });
          return newReadyArray;
        });

        return newEditedItems;
      });

      const fieldLabel =
        BULK_FIELD_OPTIONS.find((opt) => opt.field === column)?.label ?? column;
      toast(
        `${fieldLabel}: "${value}" applied to ${selectedItemsList.length} item requests`,
        {
          type: 'success',
        },
      );
    },
    [items, selectedIds],
  );

  const handleSubmitSelectedItems = useCallback(() => {
    const selectedItemsList = (items ?? []).filter((item: ItemLeadTime) =>
      selectedIds.has(item.worksheet_id),
    );

    if (selectedItemsList.length === 0) {
      toast('Selected items are no longer available', { type: 'warning' });
      clearSelection();
      return;
    }

    const validItems: SubmitBulkItemLeadTimeData[] = [];
    let invalidCount = 0;

    selectedItemsList.forEach((item: ItemLeadTime) => {
      const editedItem = editedItems.find(
        (e) => e.worksheet_id === item.worksheet_id,
      );
      const mergedItem = { ...item, ...editedItem };

      const result = UpdateBulkSchema.validate(mergedItem, {
        abortEarly: false,
        stripUnknown: true,
      });

      if (result.error) {
        handleError(
          item.worksheet_id,
          result.error.details.reduce((acc, detail) => {
            if (detail.context?.key) {
              return {
                ...acc,
                [detail.context.key]: {
                  isInvalid: true,
                  message: detail.message,
                },
              };
            }
            return acc;
          }, {}),
        );
        invalidCount += 1;
      } else {
        handleError(item.worksheet_id, {});
        validItems.push(
          normalizeSubmissionData(result.value) as SubmitBulkItemLeadTimeData,
        );
      }
    });

    if (validItems.length > 0) {
      mutate({ userId, items: validItems });
    }

    if (invalidCount > 0) {
      toast(
        `${invalidCount} ${invalidCount === 1 ? 'item' : 'items'} skipped due to errors`,
        { type: 'warning' },
      );
    }

    clearSelection();
  }, [
    items,
    editedItems,
    selectedIds,
    handleError,
    mutate,
    userId,
    clearSelection,
  ]);

  const rowPerms = useMemo(() => {
    const canSubmit = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.CREATE,
      'ITEM',
    );
    const canUpdate = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.UPDATE,
      'ITEM',
    );
    return { canSubmit, canUpdate };
  }, [currentUser.role]);

  useEffect(() => {
    setPerms(rowPerms);
  }, [rowPerms, setPerms]);

  const memoizedItems = useMemo(
    () =>
      (items ?? []).map((item: ItemLeadTime) => {
        const editedItemFound = editedItems.find(
          (editedItem) => editedItem.worksheet_id === item.worksheet_id,
        );
        if (editedItemFound) {
          return { ...item, ...editedItemFound };
        }
        return item;
      }),
    [items, editedItems],
  );

  useEffect(() => {
    if (memoizedItems?.length) {
      setAllItems(memoizedItems);
      if (selectedItemId) {
        const updatedItem = memoizedItems.find(
          (item: ItemLeadTime) => item.worksheet_id === selectedItemId,
        );
        if (updatedItem) {
          setSelectedItem(updatedItem);
        } else {
          setSelectedItem(null);
          closeDrawer();
        }
      }
    }
  }, [
    memoizedItems,
    setAllItems,
    selectedItemId,
    setSelectedItem,
    closeDrawer,
  ]);

  return (
    <>
      <PageHeaders
        currentUser={currentUser}
        userId={userId}
        requestedCount={pageHeaderInfo.requestedCount}
        completedCount={pageHeaderInfo.completedCount}
        openChatCount={pageHeaderInfo.openChatCount}
        setTab={pageHeaderInfo.setTab}
        tabValue={pageHeaderInfo.tabValue}
        searchValue={pageHeaderInfo.searchValue}
        onSearchChange={pageHeaderInfo.onSearchChange}
        showReorderChat={showReorderChat}
        submitAllButton={
          !isSuccessPage && !showReorderChat ? (
            <Button
              disabled={isLoading || isSubmitting || !isFullReady}
              onClick={handleSubmitAllItems}
              size="sm"
            >
              <Icon name="check/check-tick-circle" />
              <HideText notAbove="lg">Submit{'\u00A0'}All</HideText>
            </Button>
          ) : undefined
        }
        bulkActionToolbar={
          showReorderChat &&
          rowPerms.canUpdate &&
          rowPerms.canSubmit &&
          selectedCount > 0 ? (
            <BulkActionToolbar
              selectedCount={selectedCount}
              countrySelectItems={countrySelectItems}
              onBulkApply={handleUpdateSelectedItems}
              onSubmitAll={handleSubmitSelectedItems}
              isSubmitting={isSubmitting}
            />
          ) : undefined
        }
      />
      {isSuccessPage && (
        <RequestedItemsEmptyState
          variant={searchParam?.trim() ? 'noSearchResults' : 'allCompleted'}
        />
      )}
      {!isSuccessPage && showReorderChat && (
        <Page.Content>
          <div
            className={`transition-all duration-300 ease-in-out h-full ${
              isDrawerOpen ? 'w-[65%]' : 'w-full'
            }`}
          >
            <ItemRequestDataGrid
              data={memoizedItems}
              isLoading={isLoading}
              userId={userId}
              vendorId={vendorId}
              isCompleted={false}
              onRowClick={onRowClick}
              onMessageClick={onMessageClick}
              canUpdate={rowPerms.canUpdate}
              canSubmit={rowPerms.canSubmit}
              editedItems={editedItems}
              onUpdateItem={handleUpdateItem}
              errors={errors}
              onError={handleError}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={handleChangePage}
              onPageSizeChange={handlePageSizeChange}
              sort={sort}
              onSortChange={onSortChange}
              showBulkActions={
                showReorderChat && rowPerms.canUpdate && rowPerms.canSubmit
              }
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              isAllSelected={isAllSelected}
              isPartiallySelected={isPartiallySelected}
            />
          </div>
        </Page.Content>
      )}
      {!isSuccessPage && !showReorderChat && (
        <Page.Content>
          <div
            className={`transition-all duration-300 ease-in-out h-full ${
              isDrawerOpen ? 'w-[65%]' : 'w-full'
            }`}
          >
            <ReactVirtualizedTable
              headerCells={[
                {
                  dataKey: 'item',
                  label: 'Item',
                  width: 120,
                  fixed: true,
                },
                {
                  dataKey: 'requested_date',
                  label: 'Requested Date',
                  width: 120,
                },
                {
                  dataKey: 'cost_jr',
                  label: 'Cost JR (Required)',
                  width: 240,
                  required: true,
                },
                {
                  dataKey: 'cost_plus',
                  label: 'Cost Plus (Required)',
                  width: 240,
                  required: true,
                },
                ...(flags?.baseline_cost?.enabled
                  ? [
                      {
                        dataKey: 'baseline_cost_jr',
                        label: 'Baseline JR Cost',
                        width: 240,
                        required: true,
                        tooltip: BASELINE_COST_TOOLTIP,
                      },
                      {
                        dataKey: 'baseline_cost_plus',
                        label: 'Baseline Plus Cost',
                        width: 240,
                        required: true,
                        tooltip: BASELINE_COST_TOOLTIP,
                      },
                    ]
                  : []),
                {
                  dataKey: 'coo',
                  label: 'COO (Required)',
                  width: 120,
                  required: true,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormCoo
                          domesticEligible={domesticEligible}
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'earliest_custom_cut_delivery_date',
                  label: 'Earliest Custom Cut Delivery Date (Required)',
                  width: 240,
                  required: true,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormEarliestCustomCut
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'custom_cut_lead_time_in_weeks',
                  label: 'Custom Cut Lead Time in Weeks (Required)',
                  width: 240,
                  required: true,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormCustomCutLeadTime
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'shipment_mode',
                  label: 'Shipment Mode (Required)',
                  width: 240,
                  required: true,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormShipmentMode
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'exclusivity',
                  label: 'Exclusivity',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormExclusivity
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'stock_quantity',
                  label: 'Stock Qty',
                  width: 120,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormStockQuantity
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'stock_ratio',
                  label: 'Stock Ratio',
                  width: 120,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormStockRatio
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'earliest_IHD_date_to_deliver_stock',
                  label: 'Earliest IHD Date to Deliver Stock',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormEarliestIHD
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'incoming_stock_quantity',
                  label: 'Incoming Stock Quantity',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormIncomingStockQuantity
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'incoming_stock_date',
                  label: 'Incoming Stock Date',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormIncomingStockDate
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'incoming_stock_ratio',
                  label: 'Incoming Stock Ratio',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormIncomingStockRatio
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'custom_MOQ_jr',
                  label: 'Custom MOQ Jr',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormCustomMOQJr
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'custom_MOQ_plus',
                  label: 'Custom MOQ Plus',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormCustomMOQPlus
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'comments',
                  label: 'Other comments',
                  width: 240,
                  form: rowPerms.canUpdate
                    ? (onClose) => (
                        <FormComments
                          onSave={handleUpdateAllItems}
                          onCancel={onClose}
                        />
                      )
                    : undefined,
                },
                {
                  dataKey: 'submit',
                  label: 'Submit Item',
                  width: 200,
                },
              ]}
              rowRenderer={(index, row) => {
                const itemRow = row as ItemLeadTime;
                return (
                  <RequestedItemTableRowEditable
                    key={itemRow.worksheet_id}
                    perms={rowPerms}
                    userId={userId}
                    domesticEligible={domesticEligible}
                    itemLeadTime={itemRow}
                    loading={isLoading || isSubmitting}
                    onUpdate={handleUpdateItem}
                    handleItemValidation={handleItemValidation}
                    ready={readyArray[itemRow.worksheet_id] || false}
                    errors={errors[itemRow.worksheet_id] || {}}
                    setError={handleError}
                  />
                );
              }}
              rows={memoizedItems}
            />
            <Pagination
              count={totalCount}
              rowsPerPage={pageSize}
              page={page}
              onChangePage={handleChangePage}
            />
          </div>
        </Page.Content>
      )}
    </>
  );
}

type CompletedItemsProps = {
  currentUser: User;
  userId: string;
  vendorId: string;
  items: ItemLeadTime[];
  isLoading: boolean;
  pageHeaderInfo: {
    requestedCount: number;
    completedCount: number;
    openChatCount?: number;
    setTab: (tab: string) => void;
    tabValue: string;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
  };
  onRowClick?: (worksheetId: string) => void;
  onMessageClick?: (worksheetId: string) => void;
  isDrawerOpen?: boolean;
  showReorderChat?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  sort: SortDirection;
  onSortChange: (sort: SortDirection) => void;
};

function CompletedItems({
  currentUser,
  userId,
  vendorId,
  items,
  isLoading,
  pageHeaderInfo,
  onRowClick,
  onMessageClick,
  isDrawerOpen,
  showReorderChat = false,
  totalCount,
  page,
  pageSize,
  onChangePage,
  onChangePageSize,
  sort,
  onSortChange,
}: CompletedItemsProps) {
  if (!userId) throw new Error('Missing id path parameter');
  const { flags } = useContext(FeatureFlagsContext);
  const { setAllItems, setSelectedItem, selectedItem, setPerms, closeDrawer } =
    useRequestedItemsContext();

  const selectedItemId = selectedItem?.worksheet_id;
  useEffect(() => {
    if (items?.length) {
      setAllItems(items);
      if (selectedItemId) {
        const updatedItem = items.find(
          (item) => item.worksheet_id === selectedItemId,
        );
        if (updatedItem) {
          setSelectedItem(updatedItem);
        } else {
          setSelectedItem(null);
          closeDrawer();
        }
      }
    } else if (items && items.length === 0) {
      setAllItems([]);
      closeDrawer();
      setSelectedItem(null);
    }
  }, [items, setAllItems, selectedItemId, setSelectedItem, closeDrawer]);

  const rowPerms = useMemo(() => {
    const canUpdate = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.UPDATE,
      'ITEM',
    );
    const canSubmit = hasPermissionTo(
      'users/:id/requested-items',
      currentUser.role as RolesType,
      Actions.CREATE,
      'ITEM',
    );
    return { canUpdate, canSubmit };
  }, [currentUser.role]);

  useEffect(() => {
    setPerms(rowPerms);
  }, [rowPerms, setPerms]);

  return (
    <>
      <PageHeaders
        currentUser={currentUser}
        userId={userId}
        requestedCount={pageHeaderInfo.requestedCount}
        completedCount={pageHeaderInfo.completedCount}
        openChatCount={pageHeaderInfo.openChatCount}
        setTab={pageHeaderInfo.setTab}
        tabValue={pageHeaderInfo.tabValue}
        searchValue={pageHeaderInfo.searchValue}
        onSearchChange={pageHeaderInfo.onSearchChange}
        showReorderChat={showReorderChat}
      />
      {showReorderChat ? (
        <Page.Content>
          <div
            className={`transition-all duration-300 ease-in-out h-full ${
              isDrawerOpen ? 'w-[65%]' : 'w-full'
            }`}
          >
            <ItemRequestDataGrid
              data={items}
              isLoading={isLoading}
              userId={userId}
              vendorId={vendorId}
              isCompleted
              onRowClick={onRowClick}
              onMessageClick={onMessageClick}
              canUpdate={rowPerms.canUpdate}
              canSubmit={rowPerms.canSubmit}
              editedItems={EMPTY_EDITED_ITEMS}
              onUpdateItem={NOOP_UPDATE_ITEM}
              errors={EMPTY_ERRORS}
              onError={NOOP_ERROR}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={onChangePage}
              onPageSizeChange={onChangePageSize}
              sort={sort}
              onSortChange={onSortChange}
            />
          </div>
        </Page.Content>
      ) : (
        <Page.Content>
          <div
            className={`transition-all duration-300 ease-in-out h-full ${
              isDrawerOpen ? 'w-[65%]' : 'w-full'
            }`}
          >
            <ReactVirtualizedTable
              headerCells={[
                {
                  dataKey: 'item',
                  label: 'Item',
                  width: 120,
                  fixed: true,
                },
                {
                  dataKey: 'requested_date',
                  label: 'Requested Date',
                  width: 120,
                },
                {
                  dataKey: 'updated_at',
                  label: 'Last Update Date',
                  width: 120,
                },
                {
                  dataKey: 'cost_jr',
                  label: 'Cost JR (Required)',
                  width: 240,
                  required: true,
                },
                {
                  dataKey: 'cost_plus',
                  label: 'Cost Plus (Required)',
                  width: 240,
                  required: true,
                },
                ...(flags?.baseline_cost?.enabled
                  ? [
                      {
                        dataKey: 'baseline_cost_jr',
                        label: 'Baseline JR Cost',
                        width: 240,
                        required: true,
                        tooltip: BASELINE_COST_TOOLTIP,
                      },
                      {
                        dataKey: 'baseline_cost_plus',
                        label: 'Baseline Plus Cost',
                        width: 240,
                        required: true,
                        tooltip: BASELINE_COST_TOOLTIP,
                      },
                    ]
                  : []),
                {
                  dataKey: 'coo',
                  label: 'COO (Required)',
                  width: 120,
                  required: true,
                },
                {
                  dataKey: 'earliest_custom_cut_delivery_date',
                  label: 'Earliest Custom Cut Delivery Date (Required)',
                  width: 240,
                  required: true,
                },
                {
                  dataKey: 'custom_cut_lead_time_in_weeks',
                  label: 'Custom Cut Lead Time in Weeks (Required)',
                  width: 240,
                  required: true,
                },
                {
                  dataKey: 'shipment_mode',
                  label: 'Shipment Mode (Required)',
                  width: 240,
                  required: true,
                },
                {
                  dataKey: 'exclusivity',
                  label: 'Exclusivity',
                  width: 240,
                },
                {
                  dataKey: 'stock_quantity',
                  label: 'Stock Qty',
                  width: 120,
                },
                {
                  dataKey: 'stock_ratio',
                  label: 'Stock Ratio',
                  width: 120,
                },
                {
                  dataKey: 'earliest_IHD_date_to_deliver_stock',
                  label: 'Earliest IHD Date to Deliver Stock',
                  width: 240,
                },
                {
                  dataKey: 'incoming_stock_quantity',
                  label: 'Incoming Stock Quantity',
                  width: 240,
                },
                {
                  dataKey: 'incoming_stock_date',
                  label: 'Incoming Stock Date',
                  width: 240,
                },
                {
                  dataKey: 'incoming_stock_ratio',
                  label: 'Incoming Stock Ratio',
                  width: 240,
                },
                {
                  dataKey: 'custom_MOQ_jr',
                  label: 'Custom MOQ Jr',
                  width: 240,
                },
                {
                  dataKey: 'custom_MOQ_plus',
                  label: 'Custom MOQ Plus',
                  width: 240,
                },
                {
                  dataKey: 'comments',
                  label: 'Other comments',
                  width: 386,
                },
                {
                  dataKey: 'submit',
                  label: 'Actions',
                  width: 200,
                },
              ]}
              rowRenderer={(index, row) => {
                const itemRow = row as CompletedItemLeadTime;
                return (
                  <CompletedItemTableRow
                    perms={rowPerms}
                    key={itemRow.worksheet_id}
                    userId={userId}
                    itemLeadTime={itemRow}
                    onRowClick={onRowClick}
                  />
                );
              }}
              rows={items}
            />
            <Pagination
              count={totalCount}
              rowsPerPage={pageSize}
              page={page}
              onChangePage={onChangePage}
            />
          </div>
        </Page.Content>
      )}
    </>
  );
}

const isValidSort = (value: string | null): value is SortDirection =>
  value === 'asc' || value === 'desc';

const SEARCH_DEBOUNCE_MS = 250;

function ItemLeadTimesContent(props: ItemLeadTimesProps) {
  const { user, currentUser } = props;
  const { id: userId } = useParams<{ id: string }>();
  const location = useLocation();
  const history = useHistory();
  const urlParams = useRequestedItemsUrlParams();
  const {
    search: urlSearch,
    tab,
    page: urlPage,
    openChat,
    styleColorForChat,
    setSearch,
    setTab,
    setPage,
    updateUrl,
    clearOpenChatParams,
  } = urlParams;

  const [searchInputValue, setSearchInputValue] = useState(urlSearch);
  const setSearchRef = React.useRef(setSearch);
  setSearchRef.current = setSearch;
  const debouncedSetSearch = useMemo(
    () =>
      debounce(
        (value: string) => setSearchRef.current(value),
        SEARCH_DEBOUNCE_MS,
      ),
    [],
  );
  useEffect(() => {
    setSearchInputValue(urlSearch);
  }, [urlSearch]);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const [completedPage, setCompletedPage] = useState(1);
  const [completedPageSize, setCompletedPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setCompletedPage(1);
  }, [tab]);

  const sort: SortDirection = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get('sort');
    return isValidSort(value) ? value : 'desc';
  }, [location.search]);

  const handleSortChange = useCallback(
    (newSort: SortDirection) => {
      const params = new URLSearchParams(location.search);
      params.set('sort', newSort);
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  const handleCompletedPageSizeChange = useCallback((newPageSize: number) => {
    setCompletedPageSize(newPageSize);
    setCompletedPage(1);
  }, []);

  const handleCompletedPageChange = useCallback((newPage: number) => {
    setCompletedPage(newPage);
  }, []);

  const { isLoading, data: completedResponse } =
    useGetCompletedUserItemLeadTimes({
      userId,
      page: completedPage,
      limit: completedPageSize,
      sort,
      search: urlSearch,
    });
  const completedItems = completedResponse?.items;
  const completedTotalCount = completedResponse?.count ?? 0;

  const { flags } = useContext(FeatureFlagsContext);
  const showReorderChat = flags?.reorder_chat?.enabled ?? false;

  // Fetch needs details count — driven by search so the tab badge stays in sync
  const { data: todoCountResponse } = useGetTodoUserItemLeadTimes({
    userId,
    page: 1,
    limit: 1,
    search: urlSearch,
  });
  const todoCount = todoCountResponse?.count ?? 0;

  // Fetch open chat count only when reorder_chat feature is enabled
  const { data: openChatResponse } = useGetOpenChatItemLeadTimes({
    userId,
    page: 1,
    limit: 1,
    search: urlSearch,
    enabled: showReorderChat,
  });
  const openChatCount = showReorderChat ? (openChatResponse?.count ?? 0) : 0;

  const {
    openDrawer,
    isDrawerOpen,
    closeDrawer,
    setUserId,
    setIsCompleted,
    allItems,
  } = useRequestedItemsContext();

  const hasOpenedChatFromUrlRef = React.useRef(false);
  /**
   * Tracks whether we have already applied the deep-link navigation (tab open_chats + search
   * by styleColor) for the current openChat/styleColor URL params, so we only do it once per mount.
   */
  const hasAppliedDeepLinkTabAndSearchRef = React.useRef(false);

  useEffect(() => {
    setUserId(userId);
  }, [userId, setUserId]);

  useEffect(() => {
    closeDrawer();
  }, [tab, closeDrawer]);

  // When openChat + styleColor are in URL, navigate to open_chats tab with search so the item loads
  useEffect(() => {
    if (
      openChat &&
      styleColorForChat &&
      showReorderChat &&
      !hasAppliedDeepLinkTabAndSearchRef.current
    ) {
      hasAppliedDeepLinkTabAndSearchRef.current = true;
      updateUrl({ tab: 'open_chats', q: styleColorForChat, page: 1 });
    }
  }, [openChat, styleColorForChat, showReorderChat, updateUrl]);

  // When openChat + styleColor are in URL and the item is in allItems, open the chat drawer once
  useEffect(() => {
    if (
      !openChat ||
      !styleColorForChat ||
      !showReorderChat ||
      hasOpenedChatFromUrlRef.current ||
      allItems.length === 0
    ) {
      return;
    }
    const item = allItems.find((i) => i.style_color === styleColorForChat);
    if (item) {
      hasOpenedChatFromUrlRef.current = true;
      setIsCompleted(tab === 'completed');
      openDrawer(item.worksheet_id, 'chat');
      clearOpenChatParams();
    }
  }, [
    openChat,
    styleColorForChat,
    showReorderChat,
    allItems,
    tab,
    setIsCompleted,
    openDrawer,
    clearOpenChatParams,
  ]);

  const pageHeaderInfoBase = {
    requestedCount: todoCount,
    completedCount: completedTotalCount,
    openChatCount,
    setTab: (t: string) => setTab(t as RequestedItemsTab),
    tabValue: tab,
    searchValue: searchInputValue,
    onSearchChange: handleSearchChange,
  };

  const handleRowClick = useCallback(
    (worksheetId: string, completed = false) => {
      if (!showReorderChat) return;
      setIsCompleted(completed);
      openDrawer(worksheetId, 'quote');
    },
    [openDrawer, setIsCompleted, showReorderChat],
  );

  const handleTodoRowClick = useCallback(
    (worksheetId: string) => handleRowClick(worksheetId, false),
    [handleRowClick],
  );

  const handleCompletedRowClick = useCallback(
    (worksheetId: string) => handleRowClick(worksheetId, true),
    [handleRowClick],
  );

  const handleMessageClick = useCallback(
    (worksheetId: string, completed = false) => {
      if (!showReorderChat) return;
      setIsCompleted(completed);
      openDrawer(worksheetId, 'chat');
    },
    [openDrawer, setIsCompleted, showReorderChat],
  );

  const handleTodoMessageClick = useCallback(
    (worksheetId: string) => handleMessageClick(worksheetId, false),
    [handleMessageClick],
  );

  const handleCompletedMessageClick = useCallback(
    (worksheetId: string) => handleMessageClick(worksheetId, true),
    [handleMessageClick],
  );

  const renderTabContent = () => {
    switch (tab) {
      case 'needs_details':
        return (
          <RequestedItems
            currentUser={currentUser}
            userId={userId}
            vendorId={String(get(user, 'vendor.internal_id', ''))}
            domesticEligible={get(user, 'vendor.domestic_merch_po_eligible')}
            pageHeaderInfo={pageHeaderInfoBase}
            page={urlPage}
            onChangePage={setPage}
            search={urlSearch}
            onRowClick={handleTodoRowClick}
            onMessageClick={handleTodoMessageClick}
            isDrawerOpen={isDrawerOpen}
            showReorderChat={showReorderChat}
            sort={sort}
            onSortChange={handleSortChange}
          />
        );
      case 'open_chats':
        return (
          <RequestedItems
            variant="open_chats"
            currentUser={currentUser}
            userId={userId}
            vendorId={String(get(user, 'vendor.internal_id', ''))}
            domesticEligible={get(user, 'vendor.domestic_merch_po_eligible')}
            pageHeaderInfo={pageHeaderInfoBase}
            page={urlPage}
            onChangePage={setPage}
            search={urlSearch}
            onRowClick={handleTodoRowClick}
            onMessageClick={handleTodoMessageClick}
            isDrawerOpen={isDrawerOpen}
            showReorderChat
            sort={sort}
            onSortChange={handleSortChange}
          />
        );
      case 'completed':
        return (
          <CompletedItems
            currentUser={currentUser}
            userId={userId}
            vendorId={String(get(user, 'vendor.internal_id', ''))}
            isLoading={isLoading}
            items={completedItems || []}
            pageHeaderInfo={pageHeaderInfoBase}
            onRowClick={handleCompletedRowClick}
            onMessageClick={handleCompletedMessageClick}
            isDrawerOpen={isDrawerOpen}
            showReorderChat={showReorderChat}
            totalCount={completedTotalCount}
            page={completedPage}
            pageSize={completedPageSize}
            onChangePage={handleCompletedPageChange}
            onChangePageSize={handleCompletedPageSizeChange}
            sort={sort}
            onSortChange={handleSortChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Page.Container>{renderTabContent()}</Page.Container>
      {showReorderChat && (
        <RequestedItemsDrawer
          vendorId={String(get(user, 'vendor.internal_id', ''))}
        />
      )}
    </>
  );
}

function ItemLeadTimes(props: ItemLeadTimesProps) {
  return (
    <RequestedItemsProvider>
      <ItemLeadTimesContent {...props} />
    </RequestedItemsProvider>
  );
}

export default ItemLeadTimes;
